/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
      
      // Add keydown listener for the component
      this.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length ||
        this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      // Check if search term looks like a SKU and prioritize SKU search
      const isLikelySku = this.isLikelySku(searchTerm);
      this.getResults(searchTerm, isLikelySku);
    }

    /**
     * Check if search term resembles a SKU
     * @param {string} searchTerm - Search query
     * @returns {boolean}
     */
    isLikelySku(searchTerm) {
      // SKU patterns: alphanumeric, may contain hyphens, underscores, no spaces
      const skuPattern = /^[A-Z0-9][A-Z0-9-_]*$/i;
      return skuPattern.test(searchTerm) && searchTerm.length >= 2;
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        const isLikelySku = this.isLikelySku(query);
        this.getResults(query, isLikelySku);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        const isLikelySku = this.isLikelySku(searchTerm);
        this.getResults(searchTerm, isLikelySku);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     * @param {boolean} prioritizeSku - Whether to prioritize SKU search.
     */
    async getResults(searchTerm, prioritizeSku = false) {
      this.setLiveRegionLoadingState();

      // Define search fields - always include SKU for comprehensive search
      let searchFields = 'title,product_type,variants.title,vendor,variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      
      // Build the search query with proper encoding
      const encodedSearchTerm = encodeURIComponent(searchTerm);
      
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        // Support a pipe-separated list of product types (from the header
        // category dropdown). OR them together so the preview is scoped to the
        // whole category, e.g. (product_type:Fryer OR product_type:Fryers) AND term
        const types = this.productTypeInput.value.split('|').map((t) => t.trim()).filter(Boolean);
        const typeClause = types.map((t) => `product_type:${t}`).join(' OR ');
        const scoped = (types.length > 1 ? `(${typeClause})` : typeClause) + ` AND ${searchTerm}`;
        searchParams = `q=${encodeURIComponent(scoped)}`;
      } else {
        searchParams = `q=${encodedSearchTerm}`;
      }

      // Add search parameters for better SKU matching
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += `&${encodeURIComponent('resources[type]')}=product,collection,page,article`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit || 10}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope || 'each'}`;
      
      // Add parameter to boost SKU matches when likely searching by SKU
      if (prioritizeSku) {
        searchParams += `&${encodeURIComponent('resources[options][unquoted]')}=false`;
        searchParams += `&${encodeURIComponent('resources[options][fields_limits]')}=variants.sku:5,title:3,product_type:2,vendor:2`;
      }
      
      searchParams += '&section_id=predictive-search';

      // Debug logging
      console.log('Predictive Search Debug:');
      console.log('Search term:', searchTerm);
      console.log('Prioritize SKU:', prioritizeSku);
      console.log('Search fields:', searchFields);
      console.log('Search URL:', `${theme.routes.predictiveSearch}?${searchParams}`);

      const queryKey = searchParams.replace(/\s+/g, '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) {
          this.renderNoResults();
          return;
        }

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
        
        // Cache and render results
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
        
      } catch (error) {
        console.error('Predictive search error:', error);
        this.renderError();
      }
    }

    /**
     * Renders no results state
     */
    renderNoResults() {
      this.results.innerHTML = `
        <div class="predictive-search__no-results">
          <p>No results found for "${this.getQuery()}"</p>
        </div>
      `;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Renders error state
     */
    renderError() {
      this.results.innerHTML = `
        <div class="predictive-search__error">
          <p>Sorry, there was an error performing your search. Please try again.</p>
        </div>
      `;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
      
      // Highlight SKU matches in the results
      this.highlightSkuMatches();
    }

    /**
     * Highlights SKU matches in the search results
     */
    highlightSkuMatches() {
      const searchTerm = this.getQuery();
      const skuElements = this.results.querySelectorAll('.predictive-search__item-sku');
      
      skuElements.forEach(skuEl => {
        const skuText = skuEl.textContent.replace('SKU:', '').trim();
        if (skuText.toLowerCase().includes(searchTerm.toLowerCase())) {
          skuEl.classList.add('sku-match');
          // Move this item to the top or highlight it
          const parentItem = skuEl.closest('.predictive-search__item');
          if (parentItem && parentItem.parentElement) {
            parentItem.parentElement.prepend(parentItem);
          }
        }
      });
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.input.setAttribute('value', '');
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}

// Ensure theme settings exist with proper defaults
window.theme = window.theme || {};
theme.settings = theme.settings || {};
theme.settings.pSearchIncludeSkus = theme.settings.pSearchIncludeSkus !== false;
theme.settings.pSearchIncludeTags = theme.settings.pSearchIncludeTags || false;
theme.settings.pSearchLimit = theme.settings.pSearchLimit || 10;
theme.settings.pSearchLimitScope = theme.settings.pSearchLimitScope || 'each';
theme.routes = theme.routes || {};
theme.routes.predictiveSearch = theme.routes.predictiveSearch || '/search/suggest';

// SAFE FIX: Only add styles if they don't exist yet
if (!document.getElementById('sku-search-styles')) {
  const skuStyle = document.createElement('style');
  skuStyle.id = 'sku-search-styles';
  skuStyle.textContent = `
    .predictive-search__item-sku.sku-match { color: #ff4444; font-weight: bold; }
    .predictive-search__item[aria-selected="true"] .predictive-search__item-sku.sku-match { color: #fff; }
  `;
  document.head.appendChild(skuStyle);
}